import React, { useEffect, useState, useMemo, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import { apiClient } from "../lib/api";
import { toast } from "sonner";
import { CalendarBlank, LockKey, CheckCircle, Crown } from "@phosphor-icons/react";
import AdSlot from "../components/AdSlot";
import { useAuth } from "../context/AuthContext";

const FIXTURES_HERO = "https://images.pexels.com/photos/31741497/pexels-photo-31741497.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const fmtDate = (iso) => {
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      weekday: "short", day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
};

export default function Fixtures() {
  const { user } = useAuth();
  const [fixtures, setFixtures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [drafts, setDrafts] = useState({});
  const [saving, setSaving] = useState({});  // {fid: true} while a save is in flight
  const debounceTimers = useRef({});         // {fid: timeoutId}
  const lastSaved = useRef({});              // {fid: "h-a"} — most recent successful save

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await apiClient.get("/fixtures");
      setFixtures(data.fixtures);
      const d = {};
      data.fixtures.forEach((f) => {
        if (f.my_prediction) {
          d[f.id] = { h: f.my_prediction.home_score, a: f.my_prediction.away_score };
          lastSaved.current[f.id] = `${f.my_prediction.home_score}-${f.my_prediction.away_score}`;
        }
      });
      setDrafts(d);
    } catch (e) {
      toast.error("Failed to load fixtures");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const submit = async (fid, silent = false) => {
    const d = drafts[fid];
    if (d?.h === "" || d?.a === "" || d == null || d.h == null || d.a == null) {
      if (!silent) toast.error("Enter both scores");
      return;
    }
    const key = `${d.h}-${d.a}`;
    if (lastSaved.current[fid] === key) return;  // no change → skip
    setSaving((s) => ({ ...s, [fid]: true }));
    try {
      await apiClient.post("/predictions", {
        fixture_id: fid, home_score: d.h, away_score: d.a,
      });
      lastSaved.current[fid] = key;
      // Patch local state so the row's UI shows "Saved" without a full refetch.
      setFixtures((prev) => prev.map((f) =>
        f.id === fid
          ? { ...f, my_prediction: { ...(f.my_prediction || {}), home_score: d.h, away_score: d.a } }
          : f
      ));
      if (!silent) toast.success("Prediction saved");
      else toast.success(`Pick saved · ${key}`, { duration: 1500 });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save");
    } finally {
      setSaving((s) => ({ ...s, [fid]: false }));
    }
  };

  const setVal = (fid, k, v) => {
    const num = v === "" ? "" : Math.max(0, Math.min(20, parseInt(v, 10) || 0));
    setDrafts((prev) => {
      const nextDraft = { ...(prev[fid] || {}), [k]: num };
      const next = { ...prev, [fid]: nextDraft };
      // Auto-save when BOTH scores are filled and the pair changed from last saved.
      // Debounce ~700ms so typing "1" then "2" only fires once.
      const bothSet = nextDraft.h !== "" && nextDraft.a !== "" && nextDraft.h != null && nextDraft.a != null;
      if (bothSet) {
        const key = `${nextDraft.h}-${nextDraft.a}`;
        if (lastSaved.current[fid] !== key) {
          if (debounceTimers.current[fid]) clearTimeout(debounceTimers.current[fid]);
          debounceTimers.current[fid] = setTimeout(() => submit(fid, true), 700);
        }
      }
      return next;
    });
  };

  const groups = useMemo(() => {
    const upcoming = fixtures.filter((f) => f.status !== "finished");
    const finished = fixtures.filter((f) => f.status === "finished");
    return { upcoming, finished };
  }, [fixtures]);

  return (
    <div>
      <section className="relative overflow-hidden border-b border-white/10">
        <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${FIXTURES_HERO})` }} />
        <div className="absolute inset-0 bg-gradient-to-b from-[#090A0C]/85 to-[#090A0C]" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <span className="text-xs uppercase tracking-[0.25em] text-primary-yellow font-bold">Matchday</span>
          <h1 className="font-heading text-5xl sm:text-6xl uppercase font-black mt-3">Fixtures</h1>
          <p className="text-white/60 mt-3 max-w-xl">Set your scoreline for every match. Fixtures appear here a week before kickoff. Predictions lock at kickoff.</p>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="mb-8"><AdSlot id="fixtures-top" size="leaderboard" /></div>
        {loading && <div data-testid="fixtures-loading" className="text-white/50 text-sm uppercase tracking-widest">Loading…</div>}
        {!loading && groups.upcoming.length === 0 && groups.finished.length === 0 && (
          <div data-testid="fixtures-empty" className="border border-white/10 p-12 text-center text-white/60">
            No fixtures yet. Check back soon.
          </div>
        )}

        {groups.upcoming.length > 0 && (
          <div>
            <SectionHeader title="Upcoming" />
            <div className="border border-white/10">
              {groups.upcoming.map((f) => (
                <FixtureRow
                  key={f.id}
                  fixture={f}
                  draft={drafts[f.id]}
                  saving={saving[f.id]}
                  onChange={(k, v) => setVal(f.id, k, v)}
                  onSave={() => submit(f.id)}
                />
              ))}
            </div>
            {!user?.is_premier && !user?.is_admin && (
              <div data-testid="fixtures-premier-hint" className="mt-6 surface border border-primary-yellow/30 bg-primary-yellow/5 p-5 flex items-start gap-4">
                <div className="shrink-0 w-9 h-9 grid place-items-center bg-primary-yellow text-black rounded-full">
                  <Crown size={18} weight="fill" />
                </div>
                <div className="flex-1">
                  <div className="font-heading text-lg uppercase font-black">More fixtures locked</div>
                  <p className="text-sm text-white/60 mt-1">
                    Free Basic shows the next 7 days. Premier members see the full season&apos;s fixtures upfront — plus unlimited private leagues.
                  </p>
                </div>
                <Link
                  data-testid="fixtures-upgrade-cta"
                  to="/pricing"
                  className="shrink-0 bg-primary-yellow text-black px-4 py-3 text-xs uppercase tracking-[0.18em] font-bold hover:-translate-y-0.5 transition-transform whitespace-nowrap"
                >
                  Go Premier · £9.99
                </Link>
              </div>
            )}
          </div>
        )}

        {groups.finished.length > 0 && (
          <div className="mt-16">
            <SectionHeader title="Finished" />
            <div className="border border-white/10">
              {groups.finished.map((f) => (
                <FixtureRow key={f.id} fixture={f} finished draft={drafts[f.id]} onChange={() => {}} onSave={() => {}} />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

const SectionHeader = ({ title }) => (
  <div className="flex items-center justify-between mb-4">
    <h2 className="font-heading text-2xl uppercase font-black">{title}</h2>
    <div className="flex-1 mx-6 h-px bg-white/10" />
    <CalendarBlank size={18} className="text-white/40" />
  </div>
);

const FixtureRow = ({ fixture, draft, onChange, onSave, saving, finished }) => {
  const locked = finished || new Date(fixture.kickoff) <= new Date();
  const myPred = fixture.my_prediction;
  const h = draft?.h;
  const a = draft?.a;
  const bothSet = h !== "" && a !== "" && h != null && a != null;
  // "Dirty" = draft has BOTH scores AND differs from what's saved on the server.
  const isDirty = bothSet && (!myPred || myPred.home_score !== h || myPred.away_score !== a);
  return (
    <div data-testid={`fixture-${fixture.id}`} className="grid grid-cols-12 gap-3 items-center px-4 sm:px-6 py-5 border-b border-white/5 last:border-b-0 surface hover:bg-[#15171C] transition-colors">
      <div className="col-span-12 md:col-span-3">
        <div className="text-[10px] uppercase tracking-widest text-white/40">MW {fixture.matchweek}</div>
        <div className="text-sm text-white/70 font-mono-num">{fmtDate(fixture.kickoff)}</div>
      </div>
      <div className="col-span-12 md:col-span-5 flex items-center justify-between md:justify-center gap-4">
        <div className="text-right flex-1">
          <div className="font-heading text-lg sm:text-xl uppercase font-bold leading-tight">{fixture.home_team}</div>
        </div>
        {finished ? (
          <div className="font-heading text-3xl font-black font-mono-num px-4">
            <span className="text-primary-yellow">{fixture.home_score}</span>
            <span className="text-white/30 mx-2">·</span>
            <span className="text-primary-yellow">{fixture.away_score}</span>
          </div>
        ) : (
          <div className="flex items-center gap-2 px-2">
            <input
              data-testid={`pred-home-${fixture.id}`}
              type="number"
              inputMode="numeric"
              pattern="[0-9]*"
              min="0" max="20"
              disabled={locked}
              value={draft?.h ?? ""}
              onChange={(e) => onChange("h", e.target.value)}
              className="w-14 h-14 text-center text-2xl font-black bg-[#0B0D11] border border-white/10 focus:border-primary-yellow focus:outline-none focus:ring-1 focus:ring-primary-yellow font-mono-num disabled:opacity-60"
            />
            <span className="text-white/30 font-mono-num">·</span>
            <input
              data-testid={`pred-away-${fixture.id}`}
              type="number"
              inputMode="numeric"
              pattern="[0-9]*"
              min="0" max="20"
              disabled={locked}
              value={draft?.a ?? ""}
              onChange={(e) => onChange("a", e.target.value)}
              className="w-14 h-14 text-center text-2xl font-black bg-[#0B0D11] border border-white/10 focus:border-primary-yellow focus:outline-none focus:ring-1 focus:ring-primary-yellow font-mono-num disabled:opacity-60"
            />
          </div>
        )}
        <div className="flex-1">
          <div className="font-heading text-lg sm:text-xl uppercase font-bold leading-tight">{fixture.away_team}</div>
        </div>
      </div>
      <div className="col-span-12 md:col-span-4 flex items-center justify-end gap-3">
        {myPred && !finished && (
          <div className="text-[10px] uppercase tracking-widest text-white/50">
            Saved: <span className="text-primary-yellow font-mono-num">{myPred.home_score}-{myPred.away_score}</span>
          </div>
        )}
        {finished && myPred && (
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-widest text-white/50">Your pick {myPred.home_score}-{myPred.away_score}</div>
            <div className="font-mono-num text-2xl font-black text-primary-yellow">+{myPred.points || 0}</div>
          </div>
        )}
        {!finished && (
          locked ? (
            <span className="inline-flex items-center gap-2 text-[10px] uppercase tracking-widest text-white/40 border border-white/10 px-3 py-2">
              <LockKey size={12} /> Locked
            </span>
          ) : saving ? (
            <span
              data-testid={`pred-saving-${fixture.id}`}
              className="inline-flex items-center gap-2 text-[10px] uppercase tracking-widest text-primary-yellow border border-primary-yellow/30 px-3 py-2"
            >
              <span className="w-2 h-2 rounded-full bg-primary-yellow animate-pulse" /> Saving…
            </span>
          ) : bothSet && !isDirty ? (
            <span
              data-testid={`pred-saved-${fixture.id}`}
              className="inline-flex items-center gap-2 text-[10px] uppercase tracking-widest text-green-400/80 border border-green-400/30 px-3 py-2"
            >
              <CheckCircle size={12} weight="fill" /> Saved
            </span>
          ) : (
            <button
              data-testid={`pred-save-${fixture.id}`}
              onClick={onSave}
              disabled={!bothSet}
              className="inline-flex items-center gap-2 bg-primary-yellow text-black text-[10px] uppercase tracking-widest font-bold px-4 py-3 hover:-translate-y-0.5 transition-transform disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <CheckCircle size={14} weight="bold" />
              {myPred ? "Update" : "Save Pick"}
            </button>
          )
        )}
      </div>
    </div>
  );
};
